import { useTranslation } from 'react-i18next';

import {
	Box,
	Center,
	CheckCircleSolid,
	GiftWrapOutline,
	IconButton,
	Image,
	Popover,
	PopoverBody,
	PopoverContent,
	PopoverTrigger,
	rem,
	Text,
	VStack,
} from '@bonusly/design-system';
import { useCurrentUser } from '@bonusly/who-am-i';

import { DEFAULT_GIFT_WRAP_USES } from '../../../../config/recognition';
import { useDialogControl } from '../../../../lib/dialog';
import { getCollectibleImageUrl } from '../../../../modules/rewards/reward-details-modal/components/collectible-gallery-image';
import { useRewards } from '../../../../queries/rewards';
import { DistributionMethod } from '../../../../schemas/rewards';
import { getActiveCollectibleItem } from '../../../post-wrap/collectible-inventory';
import { DecorationButton } from './decoration-button';

export const GiftWrapButton = () => {
	const { t } = useTranslation('rewards', { keyPrefix: 'reward_details.postPurchaseCollectible' });
	const user = useCurrentUser();
	const rewardDialog = useDialogControl('reward');
	const { data: [rewards = []] = [[]] } = useRewards({ filters: { type: ['collectible'] } });

	const collectibleReward = rewards.find((r) => r.distributionMethod === DistributionMethod.Collectible);
	const activeCollectibleItem = getActiveCollectibleItem(user);

	if (!collectibleReward) return null;

	if (!activeCollectibleItem) {
		return (
			<DecorationButton
				label={t('addPostWrapTooltip')}
				onClick={() => rewardDialog.onOpen({ rewardId: collectibleReward.id, skipPostPurchase: 'true' })}
				decorationVariant="default"
			>
				<GiftWrapOutline />
			</DecorationButton>
		);
	}

	const pattern = activeCollectibleItem.variant_data.pattern ?? 'stripes';
	const color = activeCollectibleItem.variant_data.color ?? 'teal';
	const imageUrl = getCollectibleImageUrl(pattern, color);

	return (
		<Popover trigger="hover" placement="bottom" isLazy openDelay={300}>
			<PopoverTrigger>
				<Box>
					<IconButton
						aria-label={t('addPostWrapTooltip')}
						borderRadius={rem(10)}
						boxSize={rem(40)}
						color="secondary.fg"
						fontSize={rem(24)}
						_hover={{ background: 'secondary.bgAlpha' }}
						icon={
							<Box position="relative" display="inline-flex">
								<GiftWrapOutline />
								<Center position="absolute" top="-4px" right="-4px">
									<CheckCircleSolid boxSize="14px" color="primary.bg" />
								</Center>
							</Box>
						}
					/>
				</Box>
			</PopoverTrigger>
			<PopoverContent w="180px">
				<PopoverBody>
					<VStack spacing={2}>
						{imageUrl && (
							<Box borderRadius="md" overflow="hidden" w="full" h="100px">
								<Image src={imageUrl} alt={`${pattern} ${color} gift wrap`} w="full" h="full" objectFit="cover" />
							</Box>
						)}
						<Text fontSize="xs" color="text.secondary">
							{t('remainingCount', { count: activeCollectibleItem.remaining_uses, total: DEFAULT_GIFT_WRAP_USES })}
						</Text>
					</VStack>
				</PopoverBody>
			</PopoverContent>
		</Popover>
	);
};
